import React, { useEffect, useState } from "react";
import { Breadcrumbs, Divider, CardContent, Box, CardMedia, Link, Typography } from "@material-ui/core";
import clsx from "clsx";
import { Redirect, useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import { useTranslate } from "react-polyglot";
import { useBaseData } from "../../Providers/Data/BaseDataProvider.jsx";
import useTitle from "../../hooks/useTitle.js";
import useSubjectSelector from "../../hooks/useSubjectSelector.js";
import { useAuth } from "../../Providers/Auth/AuthProvider.jsx";
import useCompanyPublicId from "../../hooks/useCompanyPublicId.js";
import OfferCard from "./SharedComponents/OfferCard.jsx";
import useFavicon from "../../hooks/useFavicon.js";
import useExtractRouteParamInt from "../../hooks/useExtractRouteParamInt.js";
import CompanyLayout from "./SharedComponents/CompanyLayout.jsx";
import StyledCardBox from "../../SharedComponents/StyledCardBox.jsx";
import FlatPrimaryButton from "../../SharedComponents/FlatPrimaryButton.jsx";
import ImageSliderWithDialog from "./SharedComponents/ImageSliderWithDialog.jsx";
import LinearProgressWithLabel from "./SharedComponents/LinearProgressWithLabel.jsx";
import { ReactComponent as ArrowEnter } from "../../assets/Icons/ic_arrow_enter.svg";

const useStyles = makeStyles((theme) => ({
  mainBox: {
    width: "100%",
    minHeight: "100vh",
    backgroundColor: "#F1F2F2",
  },
  wrapper: {
    paddingBottom: 40,
    "& .MuiCardContent-root:last-child": {
      paddingBottom: 0,
    },
  },
  breadcrumbs: {
    margin: 30,

    "& .MuiBreadcrumbs-li": {
      fontSize: 16,
      fontWeight: 500,
    },
  },
  offersGrid: {
    padding: "45px 0",
  },
  smallCardBody: {
    display: "flex",
  },
  mediaSmall: {
    width: 200,
    height: 200,
    borderRadius: 16,
    overflow: "hidden",
    marginTop: 16,
    flex: "0 0 auto",
    backgroundSize: "cover",
    backgroundPosition: "center",
  },
  name: {
    fontSize: 20,
    fontWeight: 500,
    color: "#1B1C1C",
  },
  enterButton: {
    display: "flex",
    width: "70%",
    marginLeft: "auto",
    marginRight: "auto",
    textTransform: "none",
    borderRadius: 25,
    "& path": {
      stroke: ({ selectedSubject }) => (selectedSubject?.available === false ? theme.palette.primary.main : "#fff"),
    },
    "& p": {
      margin: "0 0 0 8px",
      fontSize: 22,
    },
  },
  cardContent: {
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
  },
  descriptionText: {
    color: "#3D4853",
    fontSize: 16,
    textAlign: "left",
    marginBottom: theme.spacing(2),
  },
  divider: {
    margin: "24px 0",
  },

  cardHead: {
    color: "white",
    backgroundColor: theme.palette.primary.dark,
    "& div": {
      alignSelf: "center",
    },
    "& .MuiCardHeader-content p": {
      fontWeight: 700,
    },
  },
  cardBtn: {
    margin: "8px 8px 0 0",
    "& b": {
      marginLeft: "auto",
    },
  },

  disabledBtn: {
    backgroundColor: theme.palette.primary.dark,

    opacity: 1,
    "&::after": {
      content: '"$"',
      display: "block",
      height: 25,
      width: 25,
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat",
      position: "absolute",
      top: "45%",
      transform: "translateY(-50%)",
      left: 10,
      fontSize: 18,
      fontWeight: 700,
    },
    "&.MuiButton-contained.Mui-disabled": {
      color: theme.palette.primary.main,
    },
    "&.MuiInput-underline:after": {
      left: 10,
      borderBottom: "none",
    },
    "&.MuiInput-underline.Mui-focused:after": {
      transform: "translateY(-50%)",
    },
  },
  offersBox: {
    marginTop: theme.spacing(2),
    width: "80%",
    marginLeft: "auto",
    marginRight: "auto",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
  },
}));

/** @returns {*}
 * @constructor
 */
const SubjectDetails = () => {
  const { companyName, domain } = useCompanyPublicId();
  const { subjectHandler, subjectId, selectedSubject } = useSubjectSelector();
  const classes = useStyles({ selectedSubject });
  const t = useTranslate();
  const selectedSubjectId = useExtractRouteParamInt(
    `${companyName ? "/" + companyName : ""}/subjects/:subjectId`,
    "subjectId",
  );

  const { company2, user } = useAuth();
  const {
    availableSubjects,
    chooseSubject,
    company,
    userInfo,
    setPreselectedSubject,
    setPreselectedOffer,
    schoolNotFound,
  } = useBaseData();
  const history = useHistory();

  const [selectedOffer, setSelectedOffer] = useState(/** @type {?Offer} */ null);

  const company_ = company || company2;

  const _subject = availableSubjects && availableSubjects.find((el) => el.id === selectedSubjectId);

  useTitle("title.subject", { school: (company_ && company_.name) || companyName || domain });

  useFavicon();

  useEffect(() => {
    if (_subject) {
      subjectHandler(_subject)();
    }
  }, [_subject]);

  /** */
  const enterButtonHandler = async () => {
    if (selectedOffer) {
      setPreselectedOffer(selectedOffer.id);
    }

    if (!companyName && !domain) {
      if (await chooseSubject(subjectId)) {
        history.replace("/");
      }
    } else if (user && domain) {
      if (await chooseSubject(subjectId)) {
        history.replace("/");
      }
    } else if (companyName) {
      setPreselectedSubject(subjectId);
      history.push(`/${companyName}/auth`);
    } else {
      setPreselectedSubject(subjectId);
      history.push("/auth");
    }
  };

  if (schoolNotFound) {
    return <Redirect to="/" />;
  }

  return (
    <Box className={classes.mainBox}>
      <CompanyLayout>
        <Breadcrumbs aria-label="breadcrumb" className={classes.breadcrumbs}>
          <Link
            color="inherit"
            component="button"
            variant="body1"
            onClick={() => history.push(`${companyName ? "/" + companyName : "/subjects"}`)}
          >
            {t("allSubjects")}
          </Link>
          <Typography color="textPrimary">{selectedSubject && selectedSubject.name}</Typography>
        </Breadcrumbs>
        <Box className={classes.wrapper}>
          {selectedSubject && (
            <Box>
              <StyledCardBox>
                <div className={classes.smallCardBody}>
                  <CardMedia
                    className={classes.mediaSmall}
                    image={(
                      selectedSubject.illustrationImageUrl ||
                      selectedSubject.illustrationImage?.url ||
                      company_.logo.url
                    )?.replaceAll("\\", "/")}
                  />
                  <CardContent className={classes.cardContent}>
                    <Box>
                      {selectedSubject.available === false && (
                        <Typography color="error" gutterBottom>
                          {t("subjects.unavailable")}
                        </Typography>
                      )}
                      <Typography className={classes.name} variant="body1">
                        {selectedSubject.name}
                      </Typography>
                      {selectedSubject.description && (
                        <Typography className={classes.descriptionText}>{selectedSubject.description}</Typography>
                      )}
                    </Box>
                    <Box>
                      {selectedSubject.courseProgress > 0 && (
                        <LinearProgressWithLabel value={selectedSubject.courseProgress} />
                      )}

                      <FlatPrimaryButton
                        className={clsx(classes.enterButton, {
                          [classes.disabledBtn]: selectedSubject.available === false,
                        })}
                        disabled={subjectId === 0 || selectedSubject.available === false}
                        onClick={enterButtonHandler}
                      >
                        <>
                          <ArrowEnter width="28" height="28" />
                          <p>{t("enter")}</p>
                        </>
                      </FlatPrimaryButton>
                    </Box>
                  </CardContent>
                </div>

                {selectedSubject.screenshots.length > 0 && (
                  <>
                    <Divider className={classes.divider} />
                    <ImageSliderWithDialog images={selectedSubject.screenshots} />
                  </>
                )}
              </StyledCardBox>
              {selectedSubject?.offers.length > 0 && (
                <Box className={classes.offersBox}>
                  {selectedSubject.offers.map((offer) => (
                    <OfferCard key={offer.id} offer={offer} user={userInfo} />
                  ))}
                </Box>
              )}
            </Box>
          )}
        </Box>
      </CompanyLayout>
    </Box>
  );
};

export default SubjectDetails;
